import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { useMetricsForWorkSchedule } from "components/DataGetter/FlightConfig/useFlightConfig";
import { DataDumpAggregateType } from "types/types";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import {
  useGetPageFromPath,
  useSummaryGroup,
} from "utils/customHooks/genericHooks";
import { useCallback, useMemo } from "react";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { GroupMetricType } from "components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType";
import { useParams } from "react-router";
import { ReportParams } from "reports/types";
import { getFilterMetricByFlightConfig } from "./filterMetricByFlightConfig";
import { ReportSummaryMetric } from "components/DataGetter/FlightConfig/FlightConfigTypes/ReportPageConfigType";
import { PageNameEnum } from "types/enums";
import { uniqBy } from "lodash";

/** which metrics are visible for each component?  */
export const useVisibleMetrics = (): {
  [componentName in keyof HiddenMetricsType]: GroupMetricType[];
} => {
  const [hiddenMetricsByComponent] = useHiddenMetricsByComponent();
  const metrics = useMetricsForWorkSchedule();
  const filterByFlightConfig = useFilterByFlightConfig();
  // launchdarkly flag flightGroupMetricsHideMetrics overrides stored settings
  const { flightGroupMetricsHideMetrics } = useFlagsWithDefaults();
  return useMemo(() => {
    const validMetrics = metrics.filter(m => m.dataDumpKey);
    const visibleMetrics = validMetrics.filter(filterByFlightConfig);
    const hiddenMetricsFromLD = flightGroupMetricsHideMetrics
      .split(",")
      .map(m => m.trim())
      .filter(m => m !== "");

    const visibleMetricsGroupMetrics = visibleMetrics.filter(
      m =>
        ![
          ...hiddenMetricsFromLD,
          ...hiddenMetricsByComponent.GroupMetrics,
        ].includes(m.dataDumpKey as keyof DataDumpAggregateType)
    );
    // * use validMetrics for timelineColumn instead of visibleMetrics because "onlyShowOnPages": ["timelineColumn"] config filters it out of visibleMetrics
    const timelineColumnOnlyMetrics = validMetrics.filter(m =>
      m.onlyShowOnPages?.includes("timelineColumn")
    );
    const visibleMetricsTimelineColumn = uniqBy(
      [...visibleMetrics, ...timelineColumnOnlyMetrics],
      m => m.dataDumpKey
    ).filter(
      m =>
        ![
          ...hiddenMetricsFromLD,
          ...hiddenMetricsByComponent.TimelineColumn,
        ].includes(m.dataDumpKey as keyof DataDumpAggregateType) &&
        !m.hideOnPages?.includes("timelineColumn") &&
        (!m.onlyShowOnPages ||
          m.onlyShowOnPages.includes("timelineColumn") ||
          m.onlyShowOnPages.includes(PageNameEnum.timeline))
    );
    // * use validMetrics for pnlPopup instead of visibleMetrics because "onlyShowOnPages": ["pnlPopup"] config filters it out of visibleMetrics
    const pnlPopupOnlyMetrics = validMetrics.filter(m =>
      m.onlyShowOnPages?.includes("pnlPopup")
    );
    const visibleMetricsPnlPopup = uniqBy(
      [...visibleMetrics, ...pnlPopupOnlyMetrics],
      m => m.dataDumpKey
    ).filter(
      m =>
        ![
          ...hiddenMetricsFromLD,
          ...hiddenMetricsByComponent.PnlPopup,
        ].includes(m.dataDumpKey as keyof DataDumpAggregateType) &&
        !m.hideOnPages?.includes("pnlPopup") &&
        (!m.onlyShowOnPages || m.onlyShowOnPages.includes("pnlPopup"))
    );
    return {
      GroupMetrics: visibleMetricsGroupMetrics,
      TimelineColumn: visibleMetricsTimelineColumn,
      PnlPopup: visibleMetricsPnlPopup,
    };
  }, [
    filterByFlightConfig,
    flightGroupMetricsHideMetrics,
    hiddenMetricsByComponent.GroupMetrics,
    hiddenMetricsByComponent.PnlPopup,
    hiddenMetricsByComponent.TimelineColumn,
    metrics,
  ]);
};

const hiddenMetricsByComponentAtom = atomWithStorage<HiddenMetricsType>(
  "hiddenMetricsByComponent",
  {
    GroupMetrics: [] as (keyof DataDumpAggregateType)[],
    TimelineColumn: [] as (keyof DataDumpAggregateType)[],
    PnlPopup: [] as (keyof DataDumpAggregateType)[],
  } as HiddenMetricsType
);

/** for each metric, whether it is hidden - controls found on Settings page > MetricsVisibilityControls */
export const useHiddenMetricsByComponent = (): [
  HiddenMetricsType,
  React.Dispatch<React.SetStateAction<HiddenMetricsType>>,
] => {
  const [hiddenMetricsByComponent, setHiddenMetricsByComponent] = useAtom(
    hiddenMetricsByComponentAtom
  );
  return [
    {
      GroupMetrics: hiddenMetricsByComponent.GroupMetrics ?? [],
      TimelineColumn: hiddenMetricsByComponent.TimelineColumn ?? [],
      PnlPopup: hiddenMetricsByComponent.PnlPopup ?? [],
    },
    setHiddenMetricsByComponent,
  ];
};

export type HiddenMetricsType = {
  /** shown in GroupMetrics */
  GroupMetrics: (keyof DataDumpAggregateType)[];
  /** shown in Timeline > MetricsColumns & MachineBreakdownTitleRow  */
  TimelineColumn: (keyof DataDumpAggregateType)[];
  /** shown in TrendChart > PnlPopup  */
  PnlPopup: (keyof DataDumpAggregateType)[];
};

/** filter flightConfig.customMetrics according to `hideOnPages`, `onlyShowOnPages`, `onlyShowForProcessIds` etc */
export function useFilterByFlightConfig() {
  const { pageName, isReportsPage } = useGetPageFromPath();
  const processId = useProcessIdOnSingleProcessPage();
  const { processGroupName: processGroupNameParam } = useParams<ReportParams>();
  const summaryGroup = useSummaryGroup();
  // on reports page we use processGroup from the url, on dashboard pages we use summaryGroup in the global state store

  return useCallback(
    (metric: GroupMetricType | Partial<ReportSummaryMetric>) => {
      const processGroupName = isReportsPage
        ? processGroupNameParam
        : summaryGroup;
      const filterMetric = getFilterMetricByFlightConfig({
        pageName,
        processGroupName,
        processId,
      });
      return filterMetric(metric);
    },
    [pageName, processId, isReportsPage, summaryGroup, processGroupNameParam]
  );
}
